import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchPolicies, fetchPoliciesForMultipleUsers } from "./policySlice";
import { useNavigate, useLocation } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import FlagIcon from '@mui/icons-material/Flag';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import Popover from '@mui/material/Popover';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { COMPANIES, POLICY_TYPES } from "../../data/dropdowns";
import Link from "@mui/material/Link";
import Pagination from "@mui/material/Pagination";
import { useAdminStaffData } from "../../hooks/useAdminStaffData";
import { useAccountStatus } from "../../hooks/useAccountStatus";
import StatusBadge from "../../components/common/StatusBadge";
import { calculatePolicyStatus, parseDate } from "../../utils/dateUtils";
import { exportPoliciesToExcel } from "../../utils/exportPolicies";
import MobilePolicyList from "./MobilePolicyList";
import { shouldUseMobileLayout } from "../../utils/deviceDetection";
import { getPolicyCountsByCategory } from "../../utils/countUtils";
import dayjs from "dayjs";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const getCalendarConfig = () => {
  return {
    calendarType: 'gregory',
    locale: 'en',
    maxDetail: 'month',
    minDetail: 'month'
  };
};
const PolicyList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { policies, loading } = useSelector((state) => state.policies);
  const { user } = useSelector((state) => state.auth);
  const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();
  const { isAccountActive } = useAccountStatus();

  // All hooks must be called before any conditional returns
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  // 'motor' remains the only special layout; every other category shares the non‑motor table.
  const [selectedCategory, setSelectedCategory] = useState("motor"); // initial
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedPolicyTypes, setSelectedPolicyTypes] = useState({});
  const [showCompanyOptions, setShowCompanyOptions] = useState(false);
  const [showPolicyTypeOptions, setShowPolicyTypeOptions] = useState(false);

  // Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDatePreset, setExportDatePreset] = useState('1month'); // '1month' | '3months' | '6months' | '1year' | 'custom'
  const [exportCustomStart, setExportCustomStart] = useState('');
  const [exportCustomEnd, setExportCustomEnd] = useState('');
  const [showAdvancedDates, setShowAdvancedDates] = useState(false);
  const exportEndDateRef = useRef(null);


  // Date Filter State
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [showDateRangeCalendar, setShowDateRangeCalendar] = useState(null); // 'start' or 'end'
  const calendarRef = useRef();

  const isInitialMount = useRef(true);
  const isTinyScreen = useMediaQuery('(max-height:720px)');  // e.g. 13" laptop
  const isShortScreen = useMediaQuery('(max-height:900px)'); // e.g. 14–15" laptop
  const isMediumScreen = useMediaQuery('(max-height:1080px)'); // full HD
  const itemsPerPage = isTinyScreen ? 4 : isShortScreen ? 5 : isMediumScreen ? 6 : 8;
  const categories = ["motor", "non-motor", "life"];

  const isMobile = shouldUseMobileLayout();
  const isSmallScreen = useMediaQuery('(max-width:1281px)');


  useEffect(() => {
    // Wait for useAdminStaffData to resolve admin status and staff UIDs
    if (staffLoading) return;

    if (isAdmin && userIds.length > 0) {
      // Admin user - fetch aggregated policies for all linked staff (including admin self)
      console.log('Admin user - fetching aggregated policies:', userIds);
      dispatch(fetchPoliciesForMultipleUsers(userIds));
    } else if (user?.uid && !isAdmin) {
      // Regular staff user - fetch their own policies only
      console.log('Staff user - fetching own policies');
      dispatch(fetchPolicies(user.uid));
    }
  }, [user?.uid, dispatch, isAdmin, userIds, staffLoading]);

  // Restore filter state from sessionStorage on mount
  useEffect(() => {
    const savedFilters = sessionStorage.getItem('policyList_filterState');
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        setSelectedCompanies(filters.selectedCompanies || []);
        setSelectedPolicyTypes(filters.selectedPolicyTypes || {});
        setShowCompanyOptions(filters.showCompanyOptions || false);
        setShowPolicyTypeOptions(filters.showPolicyTypeOptions || false);
        // Restore date range
        if (filters.dateRange) {
          setDateRange({
            start: filters.dateRange.start ? dayjs(filters.dateRange.start) : null,
            end: filters.dateRange.end ? dayjs(filters.dateRange.end) : null
          });
        }
      } catch (error) {
        console.error('Error restoring filter state:', error);
      }
    }
    // Delay setting isInitialMount to false to ensure all state updates complete
    setTimeout(() => {
      isInitialMount.current = false;
    }, 100);
  }, []); // Run only on mount

  // Save filter state to sessionStorage whenever filters change (but not on initial mount)
  useEffect(() => {
    // Skip saving on initial mount to avoid overwriting with empty state
    if (isInitialMount.current) {
      return;
    }

    const filterState = {
      selectedCompanies,
      selectedPolicyTypes,
      showCompanyOptions,
      showPolicyTypeOptions,
      dateRange: {
        start: dateRange.start ? dateRange.start.toISOString() : null,
        end: dateRange.end ? dateRange.end.toISOString() : null
      }
    };
    sessionStorage.setItem('policyList_filterState', JSON.stringify(filterState));
  }, [selectedCompanies, selectedPolicyTypes, showCompanyOptions, showPolicyTypeOptions, dateRange]);
  // Handle incoming navigation state to set current page and category
  useEffect(() => {
    if (location.state?.currentPage) {
      setCurrentPage(location.state.currentPage);
    }
    if (location.state?.fromCategory) {
      setSelectedCategory(location.state.fromCategory);
    }
  }, [location.state]);

  // Filter handlers
  const handleFilterButtonClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleCompanyToggle = (company) => {
    setSelectedCompanies((prev) =>
      prev.includes(company)
        ? prev.filter((c) => c !== company)
        : [...prev, company]
    );
  };

  const handlePolicyTypeToggle = (policyType) => {
    setSelectedPolicyTypes((prev) => {
      const currentTabTypes = prev[selectedCategory] || [];
      const updated = currentTabTypes.includes(policyType)
        ? currentTabTypes.filter((t) => t !== policyType)
        : [...currentTabTypes, policyType];
      return { ...prev, [selectedCategory]: updated };
    });
  };

  const handleCompanyCheckboxChange = (checked) => {
    setShowCompanyOptions(checked);
    if (!checked) {
      setSelectedCompanies([]);
    }
  };

  const handlePolicyTypeCheckboxChange = (checked) => {
    setShowPolicyTypeOptions(checked);
    if (!checked) {
      setSelectedPolicyTypes((prev) => ({ ...prev, [selectedCategory]: [] }));
    }
  };



  const clearFilters = () => {
    setSelectedCompanies([]);
    setSelectedPolicyTypes({});
    setShowCompanyOptions(false);
    setShowPolicyTypeOptions(false);
    setDateRange({ start: null, end: null });
  };

  const activeFilterCount = selectedCompanies.length + ((selectedPolicyTypes[selectedCategory] || []).length) + (dateRange.start && dateRange.end ? 1 : 0);

  // Date Range Helpers
  const handleDateRangeCalendarChange = (date) => {
    const selectedDay = dayjs(date);
    if (showDateRangeCalendar === 'start') {
      setDateRange((prev) => ({
        start: selectedDay,
        end: prev.end && selectedDay.isAfter(prev.end) ? null : prev.end
      }));
      setShowDateRangeCalendar('end');
    } else if (showDateRangeCalendar === 'end') {
      // Validation: end date cannot be before start date
      if (selectedDay.isBefore(dateRange.start, 'day')) {
        return;
      }
      setDateRange((prev) => ({
        start: prev.start,
        end: selectedDay
      }));
      setShowDateRangeCalendar(null);
    }
  };

  // Close calendar when clicking outside


  // ---- Filtering (includes vehicle number only for motor) ----
  const filtered = policies.filter((p) => {
    // Exclude new leads
    if (p.status === "New Lead") return false;

    const categoryMatch =
      selectedCategory === "motor"
        ? p.policyCategory === "motor"
        : selectedCategory === "non-motor"
          ? p.policyCategory === "non-motor"
          : p.policyCategoryType === selectedCategory;

    if (!categoryMatch) return false;

    // Company filter
    if (selectedCompanies.length > 0 && !selectedCompanies.includes(p.policyCompany)) {
      return false;
    }

    // Policy type filter (only for current tab)
    const currentTabPolicyTypes = selectedPolicyTypes[selectedCategory] || [];
    if (currentTabPolicyTypes.length > 0 && !currentTabPolicyTypes.includes(p.policyType)) {
      return false;
    }

    // Date Range Filter (createdAt) - Apply BEFORE search
    if (dateRange.start && dateRange.end) {
      const created = p.createdAt ? (p.createdAt.seconds ? dayjs(p.createdAt.seconds * 1000) : dayjs(p.createdAt)) : null;
      if (!created || !created.isValid()) return false;

      const start = dateRange.start.startOf('day');
      const end = dateRange.end.endOf('day');

      if (created.isBefore(start) || created.isAfter(end)) return false;
    }

    const searchLower = searchTerm.toLowerCase();

    // Base search keys
    const hitsBase =
      (p.name || "").toLowerCase().includes(searchLower) ||
      (p.mobile || "").includes(searchTerm);

    if (selectedCategory === "motor") {
      // Add vehicle number search for motor
      const searchClean = searchLower.replace(/-/g, "");
      const vehicleNumberClean = (p.vehicleNumber || "")
        .toLowerCase()
        .replace(/-/g, "");
      const hitVehicle = vehicleNumberClean.includes(searchClean);
      return hitsBase || hitVehicle;
    }

    return hitsBase;
  });

  // Sort by createdAt (newest first)
  const sorted = filtered.sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
    return dateB - dateA; // Descending order (newest first)
  });

  const paginated = sorted.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  // Calculate counts for each category with cross-tab filtering
  // Company and Date filters apply to all tabs, Policy Type only to current tab
  const categoryCounts = useMemo(() => {
    const counts = { motor: 0, 'non-motor': 0, life: 0 };

    policies.forEach((p) => {
      if (p.status === "New Lead") return;

      // Apply company filter (cross-tab)
      if (selectedCompanies.length > 0 && !selectedCompanies.includes(p.policyCompany)) {
        return;
      }

      // Apply date range filter (cross-tab)
      if (dateRange.start && dateRange.end) {
        const created = p.createdAt ? (p.createdAt.seconds ? dayjs(p.createdAt.seconds * 1000) : dayjs(p.createdAt)) : null;
        if (!created || !created.isValid()) return;

        const start = dateRange.start.startOf('day');
        const end = dateRange.end.endOf('day');

        if (created.isBefore(start) || created.isAfter(end)) return;
      }

      // Determine category
      if (p.policyCategory === 'motor') {
        counts.motor++;
      } else if (p.policyCategory === 'non-motor') {
        counts['non-motor']++;
      } else if (p.policyCategoryType === 'life') {
        counts.life++;
      }
    });

    return counts;
  }, [policies, selectedCompanies, dateRange]);

  const rows = paginated.map((p, index) => {
    return {
      id: p.id || index,
      name: p.name,
      mobile: p.mobile,
      policyCategoryType: p.policyCategoryType,
      vehicleMake: p.make || p.vehicleMake || "",
      vehicleNumber: p.vehicleNumber,
      policyType: p.policyType,
      policyCompany: p.policyCompany,
      status: calculatePolicyStatus(p.expiryDate, p.status),
      original: p
    };
  });

  // ---- Export handler ----
  const handleExportExcel = async () => {
    // v2: Force-reset in case isExporting got stuck from a previous failed attempt
    console.log('[Export v2] handleExportExcel called, preset:', exportDatePreset, 'customStart:', exportCustomStart, 'customEnd:', exportCustomEnd);
    setIsExporting(true);

    try {
      const now = dayjs();
      let startDate, endDate;

      if (exportDatePreset === 'custom') {
        if (!exportCustomStart || !exportCustomEnd) {
          toast.error('Please select both start and end dates');
          return;
        }

        const d1 = dayjs(exportCustomStart);
        const d2 = dayjs(exportCustomEnd);

        if (!d1.isValid() || !d2.isValid()) {
          toast.error('Invalid date selection');
          return;
        }

        // Auto-determine upper/lower bound regardless of which box has which date
        if (d1.isAfter(d2)) {
          endDate = d1.endOf('day');
          startDate = d2.startOf('day');
        } else {
          endDate = d2.endOf('day');
          startDate = d1.startOf('day');
        }
        console.log('[Export v2] Custom range — startDate:', startDate.format(), 'endDate:', endDate.format());

      } else {
        endDate = now.endOf('day');
        if (exportDatePreset === '3months') {
          startDate = now.subtract(3, 'month').startOf('day');
        } else if (exportDatePreset === '6months') {
          startDate = now.subtract(6, 'month').startOf('day');
        } else if (exportDatePreset === '1year') {
          startDate = now.subtract(1, 'year').startOf('day');
        } else {
          startDate = now.subtract(1, 'month').startOf('day');
        }
        console.log('[Export v2] Preset range — startDate:', startDate.format(), 'endDate:', endDate.format());
      }

      if (!policies || policies.length === 0) {
        toast.info('No policies found to export.');
        return;
      }

      console.log('[Export v2] Total policies before filter:', policies.length);

      const filtered = policies.filter((p) => {
        if (p.status === 'New Lead') return false;

        // Use createdAt for range filtering (expiryDate is often far in the future)
        const pDate = p.createdAt || p.expiryDate;
        if (!pDate) return false;

        let parsed;
        // Handle ISO 8601 strings like "2026-03-13T06:11:27.279Z"
        if (typeof pDate === 'string' && pDate.includes('T')) {
          parsed = new Date(pDate);
        } else {
          parsed = parseDate(pDate);
        }
        if (!parsed || isNaN(parsed.getTime())) return false;

        const targetDate = dayjs(parsed);
        if (!targetDate.isValid()) return false;

        return !targetDate.isBefore(startDate) && !targetDate.isAfter(endDate);
      });

      console.log('[Export v2] Filtered policies:', filtered.length);

      if (filtered.length === 0) {
        toast.info('No policies found for the selected date range.');
        return;
      }

      console.log('[Export v2] Calling exportPoliciesToExcel...');
      const counts = exportPoliciesToExcel(filtered);
      console.log('[Export v2] Export returned:', counts);
      toast.success(`Download started: ${counts.motorCount} motor, ${counts.nonMotorCount} other policies.`);
      setShowExportDialog(false);
    } catch (error) {
      console.error('[Export v2] Export failed:', error);
      toast.error('Export error: ' + (error.message || 'Unknown error'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      <Typography
        variant="h5"
        sx={{
          fontFamily: 'Roboto Slab',
          fontWeight: 500,
          fontSize: isSmallScreen ? '24px' : '32px',
          fontStyle: 'Medium',
          lineHeight: isSmallScreen ? '40px' : '56px',
          letterSpacing: '0.02em',
          opacity: 1,
          textEmphasisColor: '#212121',
          mb: 1
        }}
      >
        Policy
      </Typography>

      {/* Export Dialog */}
      <Dialog
        open={showExportDialog}
        onClose={() => !isExporting && setShowExportDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            p: 1,
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: 'Roboto Slab',
          fontWeight: 700,
          fontSize: '26px',
          lineHeight: '38px',
          color: '#212121',
          pb: 0.5,
        }}>
          Download Policy Data
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Roboto', fontSize: '14px', color: '#6B7280', mb: 2.5 }}>
            Select a date range and download your policy data.
          </Typography>

          {/* Date range dropdown + Advanced filter line */}
          <Typography sx={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '14px', color: '#374151', mb: 1 }}>
            Date Range
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Select
              value={exportDatePreset}
              onChange={(e) => {
                setExportDatePreset(e.target.value);
                if (e.target.value !== 'custom') {
                  setShowAdvancedDates(false);
                }
              }}
              size="small"
              sx={{
                borderRadius: '8px',
                fontSize: '14px',
                width: '50%',
                '& .MuiSelect-select': { py: 1.2 },
              }}
            >
              <MenuItem value="1month">Last 1 Month</MenuItem>
              <MenuItem value="3months">Last 3 Months</MenuItem>
              <MenuItem value="6months">Last 6 Months</MenuItem>
              <MenuItem value="1year">Last 1 Year</MenuItem>
              <MenuItem value="custom" sx={{ display: 'none' }}>Custom Range</MenuItem>
            </Select>

            <Typography
              onClick={() => {
                setShowAdvancedDates(!showAdvancedDates);
                if (!showAdvancedDates) setExportDatePreset('custom');
                else setExportDatePreset('1month');
              }}
              sx={{
                fontFamily: 'Roboto',
                fontSize: '13px',
                color: '#0063F7',
                cursor: 'pointer',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Advanced Filter
            </Typography>
          </Box>

          {/* Custom date pickers (shown when Advanced is clicked) */}
          {showAdvancedDates && (
            <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
              <TextField
                type="date"
                label="Start Date (Latest)"
                value={exportCustomStart}
                onChange={(e) => {
                  setExportCustomStart(e.target.value);
                  // Auto-open end date calendar
                  if (e.target.value) {
                    setTimeout(() => {
                      exportEndDateRef.current?.showPicker?.();
                    }, 100);
                  }
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: dayjs().format('YYYY-MM-DD') }}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                type="date"
                label="End Date (Earlier)"
                inputRef={exportEndDateRef}
                value={exportCustomEnd}
                onChange={(e) => setExportCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: exportCustomStart || dayjs().format('YYYY-MM-DD') // Cannot be after Start Date
                }}
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setShowExportDialog(false)}
            disabled={isExporting}
            variant="outlined"
            sx={{
              flex: 1,
              height: '60px',
              borderRadius: '6px',
              borderColor: '#B00020',
              color: '#B00020',
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '20px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'inherit',
                color: '#B00020',
                boxShadow: 10,
                borderColor: '#B00020'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExportExcel}
            disabled={isExporting}
            startIcon={isExporting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <DownloadIcon />}
            sx={{
              flex: 1,
              height: '60px',
              borderRadius: '6px',
              backgroundColor: '#0063F7 !important',
              fontFamily: 'Roboto',
              fontWeight: 400,
              fontSize: '20px',
              lineHeight: '24px',
              letterSpacing: '0.01em',
              textTransform: 'none',
              '&:disabled': {
                backgroundColor: '#CCCCCC !important',
                color: '#666666 !important',
              },
              '&:hover': {
                backgroundColor: '#0063F7 !important',
                color: '#FFFFFF !important',
                boxShadow: 10
              }
            }}
          >
            {isExporting ? 'Generating...' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          width: '100%',
          background: '#fff',
          borderRadius: '0',
          boxShadow: 'none',
          px: isMobile ? 0.5 : { xs: 2, sm: 3 },
          pb: isMobile ? 1 : 3,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          gap: 0,
          minHeight: 'calc(100vh - 120px)', // Ensure container takes full height
          height: 'calc(100vh - 120px)', // Fixed height to prevent shrinking
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs
            value={selectedCategory}
            onChange={(e, newVal) => {
              setSelectedCategory(newVal);
              setCurrentPage(1);
            }}
            variant="standard"
            size="small"
            indicatorColor="primary"
            sx={{
              mb: 1,
              width: 'fit-content',
              justifyContent: 'flex-start',
              '& .MuiTab-root': {
                textTransform: 'capitalize',
                fontWeight: 500,
                minWidth: isSmallScreen ? 60 : 80,
                px: isSmallScreen ? 1.5 : 2,
                py: 0.5,
                fontSize: isSmallScreen ? '13px' : 'inherit',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#E0EBFF',
                },
              }
            }}
          >
            {categories.map((cat) => (
              <Tab
                key={cat}
                label={`${cat.charAt(0).toUpperCase() + cat.slice(1)} (${categoryCounts[cat] || 0})`}
                value={cat}
                sx={{
                  '&:hover': {
                    backgroundColor: '#E0EBFF',
                  },
                }}
              />
            ))}
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={!isAccountActive}
              onClick={() => {
                setExportDatePreset('1month');
                setExportCustomStart('');
                setExportCustomEnd('');
                setShowAdvancedDates(false);
                setShowExportDialog(true);
              }}
              sx={{
                borderRadius: '8px',
                px: isSmallScreen ? 1.5 : 2.5,
                height: isSmallScreen ? 32 : 40,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: isSmallScreen ? '13px' : 'inherit',
                color: '#1F2937',
                borderColor: '#E0E0E0',
                '&:hover': {
                  color: '#004DC2',
                  backgroundColor: '#F5F8FF'
                }
              }}
            >
              Export
            </Button>
            <Badge color="primary" badgeContent={activeFilterCount} invisible={activeFilterCount === 0} sx={{ mr: 1 }}>
              <Button
                variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
                startIcon={<FilterAltOutlinedIcon />}
                onClick={handleFilterButtonClick}
                sx={{
                  borderRadius: '8px',
                  px: isSmallScreen ? 1.5 : 2.5,
                  height: isSmallScreen ? 32 : 40,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: isSmallScreen ? '13px' : 'inherit',
                  backgroundColor: activeFilterCount > 0 ? '#0063F7' : '#fff',
                  color: activeFilterCount > 0 ? '#fff' : '#1F2937',
                  borderColor: '#E0E0E0',
                  '&:hover': {
                    color: activeFilterCount > 0 ? '#fff' : '#004DC2',
                    backgroundColor: activeFilterCount > 0 ? '#0056d6' : '#F5F8FF'
                  }
                }}
              >
                Filter
              </Button>
            </Badge>
          </Box>
        </Box>
        <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 2, mt: -1 }} />


        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search client by name or phone number"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          size="small"
          InputProps={{
            startAdornment: (
              <Box sx={{ px: 1, display: 'flex', alignItems: 'center' }}>
                <FiSearch />
              </Box>
            )
          }}
          sx={{ mb: 0.5 }}
        />

        {loading ? (
          <Typography>Loading policies...</Typography>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                border: 'none',
                backgroundColor: 'transparent',
                borderRadius: 0,
                px: 0,
                mx: isMobile ? -0.5 : { xs: -2, sm: -3 }, // Extend table beyond container padding
                width: isMobile ? 'calc(100% + 8px)' : { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' }, // Compensate for negative margin
                overflow: 'visible', // Allow natural content flow without internal scrolling
                // Remove flex properties to prevent row stretching
                '& .MuiTableHead-root .MuiTableCell-root': {
                  borderBottom: '1px solid #E0E0E0',
                  backgroundColor: 'transparent',
                },
                '& .MuiTableBody-root .MuiTableCell-root': {
                  borderBottom: '1px solid #E0E0E0',
                  backgroundColor: 'transparent',
                },
                '& .MuiTable-root': {
                  borderLeft: 'none',
                  borderRight: 'none',
                },
                '& .MuiTableRow-root': {
                  borderRadius: '0 !important',
                },
                '& .MuiTableCell-root': {
                  borderRadius: '0 !important',
                },
                '& .MuiPaper-root': {
                  borderRadius: '0 !important',
                  borderTopLeftRadius: '0 !important',
                  borderTopRightRadius: '0 !important',
                  borderBottomLeftRadius: '0 !important',
                  borderBottomRightRadius: '0 !important',
                  boxShadow: 'none',
                  border: 'none',
                },
                '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                  boxShadow: 'none',
                  borderBottom: '1px solid #E0E0E0',
                },
              }}
            >
              <Table
                size="medium"
                aria-label="policy table"
                sx={{
                  border: 'none',
                  borderCollapse: 'collapse',
                  width: '100%',
                  tableLayout: isMobile ? 'auto' : 'fixed',
                  // Remove flexGrow to prevent row stretching
                  '& th, & td': {
                    border: 'none',
                    px: isMobile ? 0.5 : 0,
                  },
                  '& td:first-of-type, & th:first-of-type': {
                    pl: isMobile ? 1 : 4,
                  },
                  '& tr': {
                    borderBottom: '1px solid #E0E0E0',
                  },
                  '& tr:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ borderBottom: '1px solid #E0E0E0' }}>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #E0E0E0',
                        backgroundColor: 'transparent',
                        px: 0,
                        width: isMobile ? 60 : '10%'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 60, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <span style={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>#</span>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #E0E0E0',
                        backgroundColor: 'transparent',
                        px: 0,
                        width: isMobile ? 120 : '25%'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <PersonIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Customer
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: isMobile ? 100 : '20%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        {selectedCategory === "motor" ? (
                          <>
                            <DirectionsCarIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                            <Typography sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 500,
                              fontSize: isSmallScreen ? '12px' : '16px',
                              lineHeight: isSmallScreen ? '18px' : '24px',
                              letterSpacing: '0.01em',
                              color: '#4B5563DE',
                              opacity: 1,
                              whiteSpace: 'nowrap'
                            }}>
                              Vehicle
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CategoryIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                            <Typography sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 500,
                              fontSize: isSmallScreen ? '12px' : '16px',
                              lineHeight: isSmallScreen ? '18px' : '24px',
                              letterSpacing: '0.01em',
                              color: '#4B5563DE',
                              opacity: 1,
                              whiteSpace: 'nowrap'
                            }}>
                              Category
                            </Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: isMobile ? 120 : '25%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <DescriptionIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Policy Type
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: isMobile ? 120 : '25%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <BusinessIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Company
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: isMobile ? 80 : '20%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <FlagIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Status
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow sx={{ height: 48, verticalAlign: 'middle' }}>
                      <TableCell colSpan={6} align="center" sx={{ px: 0 }}>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <Typography sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '24px',
                            letterSpacing: '0.02em',
                            color: '#6B7280',
                            mb: 1
                          }}>
                            No policies found. Great time to follow up with new leads!
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, index) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => navigate(`/policy/${row.id}`, { state: { policy: row.original, fromPage: currentPage, fromCategory: selectedCategory } })}
                        sx={{
                          height: 48,
                          verticalAlign: 'middle',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#E0EBFF !important' }
                        }}
                      >
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 60 : '10%',
                            maxWidth: isMobile ? 60 : '10%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <div style={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '24px',
                            letterSpacing: '0.02em',
                            color: '#212121',
                            height: '48px',
                            width: '100%',
                            maxWidth: '100%',
                            display: 'flex',
                            alignItems: 'center',
                          }}>
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </div>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 120 : '25%',
                            maxWidth: isMobile ? 120 : '25%',
                            verticalAlign: 'middle',
                            overflow: 'hidden',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: '#212121',
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              opacity: 1,
                              width: '100%',
                              maxWidth: '100%',
                              display: 'block',
                            }}
                          >
                            {row.name || "-"}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '24px',
                              letterSpacing: '0.01em',
                              color: '#212121',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%',
                            }}
                          >
                            {row.mobile || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 100 : '20%',
                            maxWidth: isMobile ? 100 : '20%',
                            verticalAlign: 'middle',
                          }}
                        >
                          {selectedCategory === "motor" ? (
                            <>
                              <Typography
                                sx={{
                                  fontFamily: 'Roboto',
                                  fontWeight: 400,
                                  fontSize: '16px',
                                  lineHeight: '24px',
                                  letterSpacing: '0.02em',
                                  color: '#212121',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  opacity: 1,
                                  width: '100%',
                                }}
                              >
                                {row.vehicleMake || "-"}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: 'Roboto',
                                  fontWeight: 400,
                                  fontSize: '14px',
                                  lineHeight: '24px',
                                  letterSpacing: '0.01em',
                                  color: '#212121',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  width: '100%',
                                }}
                              >
                                {row.vehicleNumber || "-"}
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '24px',
                                letterSpacing: '0.02em',
                                color: '#212121',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                opacity: 1,
                                width: '100%',
                              }}
                            >
                              {row.policyCategoryType || "-"}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 120 : '25%',
                            maxWidth: isMobile ? 'none' : '25%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: '#212121',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              opacity: 1,
                              width: '100%',
                            }}
                          >
                            {row.policyType || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 120 : '25%',
                            maxWidth: isMobile ? 'none' : '25%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: '#212121',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              opacity: 1,
                              width: '100%',
                            }}
                          >
                            {row.policyCompany || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 80 : '20%',
                            maxWidth: isMobile ? 80 : '20%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <StatusBadge status={row.status} size={isSmallScreen ? 'small' : 'normal'} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}


                </TableBody>
              </Table>
            </TableContainer>

            {/* Filter Popover */}
            <Popover
              open={Boolean(filterAnchorEl)}
              anchorEl={filterAnchorEl}
              onClose={handleFilterClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { p: 2.5, width: 380, maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto' } } }}
            >
              <Stack spacing={2}>

                {/* Company Filter - Only show if user has multiple companies */}
                {(user?.hasMultipleCompanies !== false) && (
                  <>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={showCompanyOptions}
                              onChange={(e) => handleCompanyCheckboxChange(e.target.checked)}
                            />
                          }
                          label="Company"
                          sx={{ mb: 0 }}
                        />
                      </Box>
                      {showCompanyOptions && (
                        <Box sx={{ pl: 3, pr: 1, maxHeight: 200, overflowY: 'auto' }}>
                          {(() => {
                            const companies = selectedCategory === 'motor'
                              ? COMPANIES.motor
                              : selectedCategory === 'life'
                                ? COMPANIES.life
                                : COMPANIES.non_motor;

                            return companies.map((company) => (
                              <FormControlLabel
                                key={company}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={selectedCompanies.includes(company)}
                                    onChange={() => handleCompanyToggle(company)}
                                  />
                                }
                                label={company}
                                sx={{ mb: 0.5, display: 'block' }}
                              />
                            ));
                          })()}
                        </Box>
                      )}
                    </Box>

                    <Divider />
                  </>
                )}

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={showPolicyTypeOptions}
                          onChange={(e) => handlePolicyTypeCheckboxChange(e.target.checked)}
                        />
                      }
                      label="Policy Type"
                      sx={{ mb: 0 }}
                    />
                  </Box>
                  {showPolicyTypeOptions && (
                    <Box sx={{ pl: 3, pr: 1, maxHeight: 200, overflowY: 'auto' }}>
                      {(() => {
                        const policyTypes = selectedCategory === 'motor'
                          ? POLICY_TYPES.motor
                          : selectedCategory === 'life'
                            ? POLICY_TYPES.life
                            : POLICY_TYPES.health;

                        return policyTypes.map((policyType) => (
                          <FormControlLabel
                            key={policyType}
                            control={
                              <Checkbox
                                size="small"
                                checked={(selectedPolicyTypes[selectedCategory] || []).includes(policyType)}
                                onChange={() => handlePolicyTypeToggle(policyType)}
                              />
                            }
                            label={policyType}
                            sx={{ mb: 0.5, display: 'block' }}
                          />
                        ));
                      })()}
                    </Box>
                  )}
                </Box>

                <Divider />

                {/* Date Range Filter */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>
                    Date Range (Created)
                  </Typography>
                  <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                    <Button
                      variant={showDateRangeCalendar === 'start' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'start' ? null : 'start')}
                      sx={{
                        flex: 1,
                        '&:hover': {
                          backgroundColor: showDateRangeCalendar === 'start' ? '#0056d6' : '#E0EBFF',
                          borderColor: '#E0EBFF'
                        }
                      }}
                    >
                      {dateRange.start ? dateRange.start.format('DD MMM') : 'Start Date'}
                    </Button>
                    <Button
                      variant={showDateRangeCalendar === 'end' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setShowDateRangeCalendar(showDateRangeCalendar === 'end' ? null : 'end')}
                      sx={{
                        flex: 1,
                        '&:hover': {
                          backgroundColor: showDateRangeCalendar === 'end' ? '#0056d6' : '#E0EBFF',
                          borderColor: '#E0EBFF'
                        }
                      }}
                      disabled={!Boolean(dateRange.start)}
                    >
                      {dateRange.end ? dateRange.end.format('DD MMM') : 'End Date'}
                    </Button>
                  </Stack>

                  {showDateRangeCalendar && (
                    <Box
                      ref={calendarRef}
                      sx={{
                        position: 'relative',
                        zIndex: 1300,
                        boxShadow: 3,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        width: '100%',
                        overflow: 'hidden',
                        padding: '12px',
                        mb: 1.5,
                        minHeight: '320px',
                        maxHeight: '320px',
                        '& .react-calendar': {
                          width: '100%',
                          maxWidth: '100%',
                          minHeight: '280px',
                          '&__navigation': {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0 6px',
                            margin: '0 0 8px 0',
                            '&__label': {
                              minWidth: '180px',
                              textAlign: 'center',
                              flexGrow: 0,
                              fontWeight: '600 !important',
                              margin: 0,
                              padding: '8px 0',
                              fontSize: '16px',
                              lineHeight: '1.4'
                            },
                            '&__arrow': {
                              minWidth: '32px',
                              padding: '8px 2px',
                              margin: 0
                            }
                          },
                          '&__month-view__weekdays': {
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '0 6px',
                            marginBottom: '4px',
                            '&__weekday': {
                              width: '36px',
                              padding: '4px 0',
                              textAlign: 'center',
                              fontSize: '12px',
                              fontWeight: '600 !important',
                              textTransform: 'uppercase',
                              color: '#666 !important',
                              backgroundColor: 'transparent !important',
                              border: 'none !important',
                              '& abbr': {
                                textDecoration: 'none',
                                display: 'inline-block',
                                width: '100%',
                                textAlign: 'center'
                              }
                            }
                          },
                          '&__month-view__days': {
                            display: 'grid !important',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: '4px',
                            padding: '0 6px',
                            '&__day': {
                              width: '36px',
                              height: '36px',
                              margin: '0 auto',
                              fontWeight: '600 !important',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '13px',
                              '&--neighboringMonth': {
                                opacity: 0.5
                              },
                              '&--weekend': {
                                color: '#212121 !important'
                              },
                              '&--now': {
                                fontWeight: 'bold',
                                color: '#004DC2 !important',
                                backgroundColor: 'transparent !important',
                                '&:enabled:hover': {
                                  backgroundColor: '#E0EBFF !important'
                                }
                              }
                            }
                          }
                        },
                        '& .react-calendar__tile': {
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: '13px',
                          color: '#212121 !important',
                          transition: 'all 0.2s ease',
                          '&:enabled:hover': {
                            backgroundColor: '#E0EBFF !important',
                            color: '#212121 !important'
                          },
                          '&--active, &--active:enabled, &--active:enabled:hover': {
                            backgroundColor: '#004DC2 !important',
                            color: '#fff !important',
                            borderRadius: '50%',
                            border: 'none',
                            boxShadow: 'none',
                            outline: 'none'
                          }
                        },
                        '& .react-calendar__tile--active, & .react-calendar__tile--active:enabled': {
                          backgroundColor: '#004DC2 !important',
                          color: '#fff !important'
                        },
                        '& .react-calendar__month-view__days__day--weekend': {
                          color: '#212121 !important'
                        },
                        '& .react-calendar__tile:not(.react-calendar__tile--active):enabled:hover': {
                          backgroundColor: '#E0EBFF !important'
                        },
                        '& .react-calendar__navigation button': {
                          color: '#333 !important',
                          background: 'none !important',
                          fontWeight: '600 !important',
                          border: 'none !important',
                          outline: 'none !important',
                          '&:hover': {
                            backgroundColor: '#E0EBFF !important',
                            color: '#333 !important'
                          },
                          '&:focus': {
                            backgroundColor: 'transparent !important',
                            outline: 'none !important',
                            boxShadow: 'none !important'
                          },
                          '&:active': {
                            backgroundColor: 'transparent !important'
                          }
                        },
                        '& .react-calendar__navigation__label': {
                          color: '#333 !important',
                          background: 'transparent !important',
                          fontWeight: '600 !important',
                          '&:hover': {
                            backgroundColor: 'transparent !important'
                          },
                          '&:focus': {
                            backgroundColor: 'transparent !important',
                            outline: 'none !important'
                          }
                        }
                      }}
                    >
                      <Calendar
                        value={showDateRangeCalendar === 'start' ? dateRange.start?.toDate() : dateRange.end?.toDate()}
                        onChange={handleDateRangeCalendarChange}
                        {...getCalendarConfig()}
                      />
                    </Box>
                  )}
                </Box>
                <Divider />

                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                  <Button
                    onClick={clearFilters}
                    size="small"
                    color="inherit"
                    sx={{
                      '&:hover': {
                        backgroundColor: '#F5F5F5'
                      }
                    }}
                  >
                    Clear filters
                  </Button>
                  <Button onClick={handleFilterClose} size="small" variant="contained">
                    Done
                  </Button>
                </Stack>
              </Stack>
            </Popover>

            {/* Spacer to push pagination to bottom */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Pagination moved below */}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 'auto', // Push pagination to bottom
                  pt: 1,
                  pb: 1,
                  backgroundColor: '#fff',
                  borderRadius: '0',
                  boxShadow: 'none'
                }}
              >
                <Pagination
                  count={Math.max(totalPages, 1)}
                  page={currentPage}
                  onChange={(_, value) => setCurrentPage(value)}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: '50%',
                      border: '1px solid #D1D5DB',
                      width: 36,
                      height: 36,
                      margin: '0 4px',
                      color: '#6B7280',
                      '&:hover': {
                        backgroundColor: '#E0EBFF',
                      },
                    },
                    '& .MuiPaginationItem-previousNext': {
                      borderRadius: 0,
                      border: 'none',
                      backgroundColor: 'transparent',
                      width: 'auto',
                      height: 'auto',
                      margin: '0 4px',
                      color: '#6B7280',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    },
                    '& .Mui-selected': {
                      backgroundColor: '#004DC2 !important',
                      color: '#fff !important',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: '#004DC2 !important',
                      },
                    },
                  }}
                />
              </Box>
            )}
          </>
        )
        }
      </Box >

    </Box >
  );
};

export default PolicyList;
